/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2019] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

var _ = require('underscore');
var _get = require('lodash.get');
var moment = require('moment');

var cleanUpResponse = require('common/util/cleanUpResponse');

var isNonDepAmendmentTask = require('../../common/util/isNonDepAmendmentTask');

module.exports = function() {
  var deferred = $.Deferred();

  $.soap({
    body: {
      GetAssignedTasks: {
        $xmlns: 'http://dobs.pa.gov/nd/task/bpm/1.0'
      }
    },
    blueprintArrays: [
      'GetAssignedTasksResponse.Tasks.NOTF_TASK_INSTANCE',
      'GetAssignedTasksResponse.Applications.ND_Application',
      'GetAssignedTasksResponse.TaskTypes.CM_LU_TaskType',
      'GetAssignedTasksResponse.Exams.ND_Exam',
      'GetAssignedTasksResponse.Compliances.ND_Compliance',
      'GetAssignedTasksResponse.Correspondences.ND_Correspondence'
    ],
    blueprint: {
      tasks: [
        '$..NOTF_TASK_INSTANCE',
        {
          taskID: '$..TaskId',
          deliveryDate: '$..DeliveryDate',
          entityId: '$..EntityID',
          applicationId: '$..ApplicationID',
          examId: '$..ExamID',
          complianceId: '$..ComplianceID',
          taskTypeName: '$..TaskTypeName',
          claimedDate: '$..ClaimedDate',
          correspondenceId: '$..CorrespondenceID'
        }
      ],
      taskTypes: [
        '$..CM_LU_TaskType',
        {
          id: '$.ID',
          name: '$.Name',
          description: '$.Description'
        }
      ],
      applications: [
        '$..ND_Application',
        {
          id: '$..ApplicationID',
          entityName: '$..EntityName',
          applicationType: '$..ApplicationType',
          applicationCategory: '$..ApplicationCategory',
          licenseType: '$..LicenseType',
          licenseNumber: '$..LicenseNumber',
          licenseId: '$..LicenseID',
          address: {
            addressLine1: '$..AddressLine1',
            addressLine2: '$..AddressLine2',
            addressLine3: '$..AddressLine3',
            city: '$..City',
            state: '$..State',
            postalCode: '$..PostalCode',
            country: '$..Country'
          }
        }
      ],
      exams: [
        '$..ND_Exam',
        {
          id: '$..ExamID',
          entityName: '$..EntityName',
          licenseType: '$..LicenseType',
          licenseNumber: '$..LicenseNumber',
          licenseId: '$..LicenseID',
          address: {
            addressLine1: '$..AddressLine1',
            addressLine2: '$..AddressLine2',
            addressLine3: '$..AddressLine3',
            city: '$..City',
            state: '$..State',
            postalCode: '$..PostalCode',
            country: '$..Country'
          }
        }
      ],
      compliances: [
        '$..ND_Compliance',
        {
          id: '$..ComplianceID',
          entityName: '$..EntityName',
          entityId: '$..EntityID',
          licenseType: '$..LicenseType',
          licenseId: '$..LicenseID',
          operatingAsType: '$..OperatingAsLicenseType',
          licenseNumber: '$..LicenseNumber',
          address: {
            addressLine1: '$..AddressLine1',
            addressLine2: '$..AddressLine2',
            addressLine3: '$..AddressLine3',
            city: '$..City',
            state: '$..State',
            postalCode: '$..PostalCode',
            country: '$..Country'
          }
        }
      ],
      correspondences: [
        '$..ND_Correspondence',
        {
          correspondenceId: '$..CorrespondenceID',
          entityName: '$..EntityName',
          licenseType: '$..LicenseType',
          licenseNumber: '$..LicenseNumber',
          licenseId: '$..LicenseID',
          applicationCategory: '$..ApplicationCategory',
          applicationType: '$..ApplicationType',
          address: {
            addressLine1: '$..AddressLine1',
            addressLine2: '$..AddressLine2',
            addressLine3: '$..AddressLine3',
            city: '$..City',
            state: '$..State',
            postalCode: '$..PostalCode',
            country: '$..Country'
          }
        }
      ]
    }
  })
    .done(function(response) {
      cleanUpResponse(response);
      // Create a moment object for each draft app's last modified date
      _.each(response.tasks, function(task) {
        task.deliveryDate = moment.utc(task.deliveryDate, moment.ISO_8601).local();
        task.claimedDate = task.claimedDate ? moment(task.claimedDate, moment.ISO_8601) : undefined;

        // The Assigned/Claimed date represents when a task started belonging
        // to a specific user.
        task.assignedClaimedDate = task.deliveryDate;
        if (task.claimedDate && task.claimedDate.isValid()) {
          task.assignedClaimedDate = task.claimedDate;
        }

        var type = _.find(response.taskTypes, function(type) {
          return task.taskTypeName === type.name;
        });
        task.type = type.description;

        if (task.applicationId) {
          var application = _.find(response.applications, function(application) {
            return task.applicationId === application.id;
          });
          if (application) {
            task.applicationType = application.applicationCategory || '--';
            task.entityName = application.entityName;
            task.licenseType = application.licenseType || '--';
            task.licenseNumber = application.licenseNumber || '--';
            task.licenseId = application.licenseId;
            task.location = _get(application, 'address.addressLine1', '--') || '--';
            task.city = _get(application, 'address.city','--') || '--';
            task.state = _get(application, 'address.state','--') || '--';
            var isAmendmentTask = isNonDepAmendmentTask(type.id);
            if (isAmendmentTask) {
              task.type += ' - ' + application.applicationType;
            }
          }
        } else if (task.examId) {
          /* Handle Exam Meta Data */
          var exam = _.find(response.exams, function(exam) {
            return task.examId === exam.id;
          });
          if (exam) {
            task.applicationType = '--';
            task.entityName = exam.entityName;
            task.licenseType = exam.licenseType || '--';
            task.licenseId = exam.licenseId;
            task.licenseNumber = exam.licenseNumber || '--';
            task.location = _get(exam, 'address.addressLine1', '--') || '--';
            task.city = _get(exam, 'address.city','--') || '--';
            task.state = _get(exam, 'address.state','--') || '--';
          }
        } else if (task.complianceId) {
          /* Handle Compliance Meta Data */
          var compliance = _.find(response.compliances, function(compliance) {
            return task.complianceId === compliance.id;
          });
          if (compliance) {
            task.applicationType = '--';
            task.entityId = compliance.entityId;
            task.entityName = compliance.entityName;
            task.licenseId = compliance.licenseId;
            task.licenseType = compliance.licenseType || compliance.operatingAsType || '--';
            task.licenseNumber = compliance.licenseNumber || '--';
            task.location = _get(compliance, 'address.addressLine1', '--') || '--';
            task.city = _get(compliance, 'address.city','--') || '--';
            task.state = _get(compliance, 'address.state','--') || '--';
          }
        } else if (task.correspondenceId) {
          var correspondence = _.find(response.correspondences, function(correspondence) {
            return task.correspondenceId === correspondence.correspondenceId;
          });
          if (correspondence) {
            task.applicationType = correspondence.applicationType || '--';
            task.entityName = correspondence.entityName;
            task.licenseId = correspondence.licenseId;
            task.licenseType = correspondence.licenseType || '--';
            task.licenseNumber = correspondence.licenseNumber || '--';
            task.location = _get(correspondence, 'address.addressLine1', '--') || '--';
            task.city = _get(correspondence, 'address.city','--') || '--';
            task.state = _get(correspondence, 'address.state','--') || '--';
          }
        }
      });

      deferred.resolve(response.tasks);
    })
    .fail(function(err) {
      deferred.reject();
    });

  return deferred.promise();
};
